import * as React from 'react'
import { useState } from 'react'
import { formatCurrency, formatDate } from "../../common/util"
import PaymentBadge from '../../common/PaymentBadge';
import ZoneBadge from '../../common/ZoneBadge';
import { Grid, Box, Button, CircularProgress, CardContent, Card, CardHeader } from '@mui/material';
import { useGetAllOrdersQuery } from '../../redux/commonApiCalls';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFDateRangePickerField, RHFTextField } from '../../common/hook-form';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import Loader from '../../common/Loader';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorIcon from '@mui/icons-material/Error';
import BorderColorIcon from '@mui/icons-material/BorderColor';

import { apiConstants, post } from '../../common/constants';
import SideDrawer from '../../components/SideDrawer';
import ProcessUnprocessedOrder from './ProcessUnprocessedOrder';
import MUIModal from '../../components/MuiModal';
import { MerchantFilterCached } from '../../common/ProshipFormComponents';
import PageTitle from '../../components/PageTitle';
import OrderRowActions from '../../common/OrderRowActions';
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, ellipsis }) => (
    <Box sx={{
        fontSize: 12.5, lineHeight: 1.4,
        color: muted ? 'text.secondary' : 'text.primary',
        fontWeight: bold ? 600 : 400,
        ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
        {children}
    </Box>
);

const CellStack = ({ children }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
        {children}
    </Box>
);



// ─── Constants ────────────────────────────────────────────────────────────────

const defaultFilters = [
    { field: "orderSubStatus", operator: "eq", value: "UNPROCESSED" },
    { field: "orderType", operator: "eq", value: "FORWARD SHIPMENT" }
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UnprocessedOrders() {
    const [page, setPage] = useState(0);
    const [filters, setFilters] = React.useState([]);
    const [sizePerPage, setSizePerPage] = useState(20);
    const [logRow, setLogRow] = React.useState(null);
    const [open, setOpen] = useState(false);
    const [colRow, setRow] = useState([]);
    const [remarkRow, setRemarkRow] = useState(null);

    const exclude = "pickupDetails,returnInfo,itemList,deliveryDetails,shipmentDetail,courier,merchant,version,order_history,itemList,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,actualCourierProviderName,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,channelName,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchantPricing.pricingObj,merchantPricing.merchantRateCard,modifiedByUser,lastModifiedDate,terminalStatus";

    const { data: allRows, isLoading, isFetching } = useGetAllOrdersQuery({
        "limit": sizePerPage,
        "offset": (page) * sizePerPage,
        "andfilter": [...defaultFilters, ...filters],
        "excludes": exclude,
        "skipCount": true,
    });

    const methods = useForm({
        defaultValues: {
            merchant: '',
            reference: '',
            awb: '',
            city: '',
            state: '',
            fromDate: '',
            toDate: ''
        }
    });
    const { handleSubmit, reset } = methods;

    const onSubmit = (filters) => {
        let andfilter = [];
        if (filters.city) {
            andfilter.push({ "field": "deliveryDetails.to_city", "operator": "eq", "value": filters.city });
        }
        if (filters.state) {
            andfilter.push({ "field": "deliveryDetails.to_state", "operator": "eq", "value": filters.state });
        }
        if (filters.merchant) {
            andfilter.push({ "field": "merchant.$id", "operator": "eq", "value": 'ObjectId(\'' + filters.merchant + '\')' });
        }
        if (filters.reference) {
            andfilter.push({ "field": "reference", "operator": "like", "value": filters.reference + ".*" });
        }
        if (filters.orderId) {
            andfilter.push({ "field": "orderId", "operator": "like", "value": filters.orderId });
        }
        if (filters.fromDate) {
            andfilter.push({ field: "orderDate", operator: "gte", value: formatDate(filters.fromDate, 'yyyy-MM-dd') });
        }
        if (filters.toDate) {
            andfilter.push({ field: "orderDate", operator: "lte", value: formatDate(filters.toDate, 'yyyy-MM-dd') });
        }
        setFilters(andfilter);
    };

    const handleClickOpen = (row) => { setOpen(true); setRow(row); };
    const handleClose = () => { setOpen(false); };

    const columns = [
        {
            field: 'orderId',
            headerName: 'Order ID', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 170, flex: 1,
            renderCell: ({ row }) => (
                <CellStack>
                    <CellLine bold>{row.orderId || '—'}</CellLine>
                    {row?.additionalData?.integrationChannel === 'Unicom' && row.reference && (
                        <Box sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            Unicom Ship ID: {row.reference}
                        </Box>
                    )}
                </CellStack>
            ),
        },
        {
            field: 'invoice_value',
            headerName: 'Invoice Value', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 120, flex: 0.8,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    {formatCurrency(row.invoice_value)}
                </Box>
            ),
        },
        {
            field: 'merchant.name',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, flex: 1,
            renderCell: ({ row }) => (
                <CellLine bold>{row?.merchantDetail?.name || '—'}</CellLine>
            ),
        },
        {
            field: 'remark',
            headerName: 'Remark', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 220, flex: 2.5,
            renderCell: ({ row }) => {
                const remark = row.remark || '';
                const truncated = remark.length > 100;
                return (
                    <Box sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {truncated ? remark.slice(0, 100) + '…' : remark || '—'}
                        {truncated && (
                            <Box
                                component="button"
                                onClick={() => setRemarkRow(remark)}
                                sx={{
                                    display: 'inline', ml: '6px', fontSize: '11px', fontWeight: 600,
                                    color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer',
                                    p: 0, textDecoration: 'underline', '&:hover': { color: '#1e40af' },
                                }}
                            >
                                View more
                            </Box>
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'merchantZone',
            headerName: 'Zone', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => <ZoneBadge zone={row.merchantZone} />,
        },
        {
            field: 'shippingLabelId',
            headerName: 'Shipping Label', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140, flex: 0.9,
            renderCell: ({ row }) => (
                <CellLine muted ellipsis>{row.shippingLabelId || '—'}</CellLine>
            ),
        },
        {
            field: 'payment_mode',
            headerName: 'MOP', headerAlign: 'left', align: 'left',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => <PaymentBadge mode={row.payment_mode} />,
        },
        {
            field: 'created',
            headerName: 'Created Date', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 130, flex: 0.9,
            renderCell: ({ row }) => (
                <CellLine muted>{row.createdDate ? formatDate(row.createdDate) : '—'}</CellLine>
            ),
        },
        {
            field: 'action',
            headerName: '', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 60,
            renderCell: ({ row }) => (
                <OrderRowActions
                    actions={[
                        {
                            key: 'view',
                            label: 'View details',
                            icon: <VisibilityIcon fontSize="small" />,
                            href: `/unprocessedorder-detail/${row.id}/${row?.merchantDetail?.key}`,
                        },
                        {
                            key: 'process',
                            label: 'Process order',
                            icon: <BorderColorIcon fontSize="small" />,
                            onClick: () => handleClickOpen(row),
                            color: 'primary',
                        },
                        {
                            key: 'logs',
                            label: 'View error logs',
                            icon: <ErrorIcon fontSize="small" />,
                            onClick: () => setLogRow(row),
                            color: 'error',
                            dividerBefore: true,
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <>
            <PageTitle>Forward Stuck Orders</PageTitle>
            {(isLoading || isFetching) && <Loader />}

            {/* ── Filter bar ────────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Reference Number</FieldLabel>
                                <RHFTextField name="reference" label="" placeholder="Reference Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Order ID</FieldLabel>
                                <RHFTextField name="orderId" label="" placeholder="Order ID" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>City</FieldLabel>
                                <RHFTextField name="city" label="" placeholder="City" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>State</FieldLabel>
                                <RHFTextField name="state" label="" placeholder="State" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled />
                            </FilterField>

                            <FilterDateField>
                                <FieldLabel>Created Date</FieldLabel>
                                <RHFDateRangePickerField name="created_date" label="" placeholder="Select date range" size="small" maxDate={new Date()} />
                            </FilterDateField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Button type="submit" variant="contained" size="small"
                                    sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </Button>
                                <Button type="button" variant="contained" size="small"
                                    sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                                    onClick={() => {
                                        reset({ merchant: '', reference: '', orderId: '', city: '', state: '' });
                                        setFilters([]);
                                    }}>
                                    Reset
                                </Button>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={allRows?.data || []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    rowCount={Number.MAX_VALUE}
                    getRowId={(row) => row.id}
                />
            </Box>

            <MUIModal
                open={!!remarkRow}
                handleClose={() => setRemarkRow(null)}
                title="Full Remark"
                maxWidth="sm"
            >
                <Box sx={{ fontSize: '13px', color: 'text.primary', lineHeight: 1.7, p: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {remarkRow}
                </Box>
            </MUIModal>

            <MUIModal
                open={open}
                handleClose={handleClose}
                children={<ProcessUnprocessedOrder row={colRow} setOpen={setOpen} open={open} />}
                title={"Please select the Serviceable Courier partner"}
            />

            {logRow && (
                <SideDrawer open={true} setOpen={() => setLogRow(null)} title="Error Logs">
                    <RowLogs row={logRow} />
                </SideDrawer>
            )}
        </>
    );
}

// ─── RowLogs — unchanged ──────────────────────────────────────────────────────

function RowLogs({ row }) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    React.useEffect(() => {
        setIsLoading(true);
        post(apiConstants.TOOL_LOGS, {
            andfilter: [{ field: "orderReference", operator: "eq", value: row.reference }],
            offset: 0,
            limit: 10,
            sort: { field: "time", direction: "DESC" }
        })
            .then((res) => { setData(res.data); setIsLoading(false); })
            .catch((err) => { console.error(err); setIsLoading(false); });
    }, [row]);

    return (
        <Grid container spacing={1} sx={{ padding: '15px' }}>
            {isLoading && <CircularProgress size={20} />}
            {!isLoading && data?.data.map((log, index) => (
                <Grid item xs={12} key={index}>
                    <Card sx={{ borderRadius: 1, backgroundColor: '#f9fafb' }}>
                        <CardHeader
                            sx={{ p: 1, pb: 0, '& span': { fontSize: '12px !important' } }}
                            title={`${index + 1}. ${log.accountCode}, ${formatDate(log.time)}`}
                        />
                        <CardContent sx={{ p: 1 }}>
                            <code style={{ color: 'inherit', fontSize: '12px' }}>{log.response}</code>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
}
